import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './SheetMapper.css';
import Map from '../components/Map';
import DeckGLMap from '../components/DeckGLMap';
import Sidebar from '../components/Sidebar';
import SyncButton from '../components/SyncButton';
import SheetSetupDialog from '../components/SheetSetupDialog';
import RecentSheetsTiles from '../components/RecentSheetsTiles';
import RecentSheetsSidebar from '../components/RecentSheetsSidebar';
import { fetchSheetData, syncToSheet, fetchSheetTabs, getRecentSheets, saveRecentSheet, pinSheet, unpinSheet, updateSheetName, createSharedMap } from '../services/api';
import useIsMobile from '../hooks/useIsMobile';
import { getColorAssignmentsObject, restoreColorAssignments, getHiddenGroupsArray, restoreHiddenGroups } from '../utils/markerUtils';

// Toggle between Map and DeckGLMap
const USE_DECKGL = true;

function SheetMapper() {
  console.log('App component rendering');
  const location = useLocation();
  const [sheetUrl, setSheetUrl] = useState('');
  const [markers, setMarkers] = useState([]);
  const [rawSheetData, setRawSheetData] = useState(null);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showTabSelectorDialog, setShowTabSelectorDialog] = useState(false);
  const [availableSheets, setAvailableSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [sheetConfig, setSheetConfig] = useState(null);
  const [selectedMarkers, setSelectedMarkers] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editedMarkerIds, setEditedMarkerIds] = useState(new Set());
  const [editedFields, setEditedFields] = useState({}); // { markerId: { field: value, ... } }
  const [selectionMode, setSelectionMode] = useState(null);
  const [visibleMarkers, setVisibleMarkers] = useState([]); // Markers after group filtering
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    // Load from localStorage or default to 320px
    const saved = localStorage.getItem('sidebarWidth');
    return saved ? parseInt(saved, 10) : 320;
  });
  const [colorRefreshKey, setColorRefreshKey] = useState(0);
  const [pinnedSheets, setPinnedSheets] = useState([]);
  const [recentSheets, setRecentSheets] = useState([]);
  const isMobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);
  console.log('App state initialized');

  // Leaving mobile layout should not leave drawer state stuck
  useEffect(() => {
    if (!isMobile) setFiltersOpen(false);
  }, [isMobile]);

  // Sync visibleMarkers with markers when markers change
  useEffect(() => {
    setVisibleMarkers(markers);
  }, [markers]);

  // Load recent sheets on mount
  useEffect(() => {
    const loadRecentSheets = async () => {
      try {
        const data = await getRecentSheets();
        setPinnedSheets(data.pinned || []);
        setRecentSheets(data.recent || []);
      } catch (error) {
        console.error('Failed to load recent sheets:', error);
        // Silently fail - user can still use URL input
      }
    };
    loadRecentSheets();
  }, []);

  // Auto-load shared map from query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('url') && params.get('config')) {
      const sharedUrl = params.get('url');
      const sharedTab = params.get('tab') || '';
      const sharedMapName = params.get('mapName');

      setSheetUrl(sharedUrl);
      setSelectedSheet(sharedTab);
      setLoading(true);
      setError(null);

      try {
        const sharedConfig = JSON.parse(params.get('config'));
        setSheetConfig(sharedConfig);

        // Restore color assignments and hidden groups if saved
        if (sharedConfig.colorAssignments) {
          restoreColorAssignments(sharedConfig.colorAssignments);
        }
        if (sharedConfig.hiddenGroups) {
          restoreHiddenGroups(sharedConfig.hiddenGroups);
        }

        const columnMapping = {
          latitude: sharedConfig.latColumn,
          longitude: sharedConfig.lngColumn,
        };

        if (sharedConfig.editableColumns) {
          sharedConfig.editableColumns.forEach(col => {
            columnMapping[col] = col;
          });
        }

        fetchSheetData(sharedUrl, columnMapping, sharedTab)
          .then(data => {
            setMarkers(data.markers);
            setRawSheetData(data);
            setVisibleMarkers(data.markers);

            // Save to recent sheets with custom name if provided
            const colorAssignments = getColorAssignmentsObject();
            const hiddenGroups = getHiddenGroupsArray();
            saveRecentSheet({
              url: sharedUrl,
              sheetName: sharedMapName || data.sheetName || 'Untitled Sheet',
              tabName: sharedTab,
              config: { ...sharedConfig, colorAssignments, hiddenGroups }
            }).then(updatedRecent => {
              setPinnedSheets(updatedRecent.pinned || []);
              setRecentSheets(updatedRecent.recent || []);
            }).catch(error => {
              console.error('Failed to update recent sheets:', error);
            });

            setLoading(false);
          })
          .catch(err => {
            console.error('Failed to load sheet:', err);
            setError(err.message || 'Failed to load sheet');
            setLoading(false);
          });
      } catch (err) {
        console.error('Failed to parse shared map config:', err);
        setError('Failed to load shared map');
        setLoading(false);
      }
    }
  }, [location]);

  const handleLoadSheet = async () => {
    if (!sheetUrl.trim()) {
      setError('Please enter a Google Sheets URL');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First, fetch available tabs/sheets
      const tabsData = await fetchSheetTabs(sheetUrl);

      if (tabsData.sheets && tabsData.sheets.length > 1) {
        // Multiple sheets - show tab selector dialog
        setAvailableSheets(tabsData.sheets);
        setSelectedSheet(tabsData.sheets[0].name); // Default to first sheet
        setShowTabSelectorDialog(true);
        setLoading(false);
        return;
      }

      // Single sheet - proceed directly
      const sheetName = tabsData.sheets && tabsData.sheets.length === 1 ? tabsData.sheets[0].name : null;
      setSelectedSheet(sheetName || '');
      await loadSheetData(sheetName);
    } catch (err) {
      console.error('Failed to load sheet:', err);

      if (err.needsSheetsScope) {
        setError('Google Sheets access is required. Use Allow Google Sheets on the access screen, then try again.');
      } else if (err.needsAuth) {
        setError('Authentication required. Please sign in with Google.');
      } else {
        setError(err.message || 'Failed to load sheet data');
      }
      setLoading(false);
    }
  };

  const loadSheetData = async (sheetName) => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchSheetData(sheetUrl, null, sheetName);
      setRawSheetData(data);
      // Always show dialog for styling and editable column configuration
      setShowSetupDialog(true);
      setHasUnsavedChanges(false);
      setEditedMarkerIds(new Set());
      setEditedFields({});
    } catch (err) {
      console.error('Failed to load sheet:', err);

      if (err.needsSheetsScope) {
        setError('Google Sheets access is required. Use Allow Google Sheets on the access screen, then try again.');
      } else if (err.needsAuth) {
        setError('Authentication required. Please sign in with Google.');
      } else if (err.needsMapping && err.headers) {
        // Show setup dialog for manual column mapping
        setRawSheetData({ headers: err.headers, markers: [] });
        setShowSetupDialog(true);
      } else {
        setError(err.message || 'Failed to load sheet data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = useCallback(async (config) => {
    setSheetConfig(config);
    setLoading(true);

    try {
      // Fetch sheet data again with the column mapping
      const columnMapping = {
        latitude: config.latColumn,
        longitude: config.lngColumn,
      };

      const data = await fetchSheetData(sheetUrl, columnMapping, selectedSheet || null);

      setMarkers(data.markers);
      setRawSheetData(data);
      setShowSetupDialog(false);
      setHasUnsavedChanges(false);
      setEditedMarkerIds(new Set());
      setEditedFields({});

      // Save to recent sheets with color assignments and hidden groups
      try {
        const colorAssignments = getColorAssignmentsObject();
        const hiddenGroups = getHiddenGroupsArray();
        const updatedRecent = await saveRecentSheet({
          url: sheetUrl,
          sheetName: data.sheetName || 'Unnamed Sheet',
          tabName: selectedSheet || 'Sheet1',
          config: { ...config, colorAssignments, hiddenGroups }
        });
        setPinnedSheets(updatedRecent.pinned || []);
        setRecentSheets(updatedRecent.recent || []);
      } catch (error) {
        console.error('Failed to save to recent sheets:', error);
        // Don't block the main flow if this fails
      }
    } catch (err) {
      console.error('Failed to load sheet with mapping:', err);
      setError(err.message || 'Failed to load sheet data');
    } finally {
      setLoading(false);
    }
  }, [sheetUrl, selectedSheet]);

  const handleSetupCancel = useCallback(() => {
    setShowSetupDialog(false);
    // Optionally clear markers if user cancels
    // setMarkers([]);
  }, []);

  const handleTabSelectorComplete = useCallback(async () => {
    setShowTabSelectorDialog(false);
    await loadSheetData(selectedSheet);
  }, [selectedSheet, sheetUrl]);

  const handleTabSelectorCancel = useCallback(() => {
    setShowTabSelectorDialog(false);
  }, []);

  const handleMarkerUpdate = useCallback((markerId, updates) => {
    setMarkers(prev => prev.map(marker =>
      marker.id === markerId ? { ...marker, ...updates } : marker
    ));
    setEditedMarkerIds(prev => new Set([...prev, markerId]));

    // Track field-level changes
    setEditedFields(prev => ({
      ...prev,
      [markerId]: {
        ...(prev[markerId] || {}),
        ...updates
      }
    }));

    setHasUnsavedChanges(true);
  }, []);

  const handleOpenSettings = useCallback(() => {
    // Reopen the setup dialog with current configuration
    setShowSetupDialog(true);
  }, []);

  const handleSync = async () => {
    setLoading(true);
    setError(null);

    try {
      // Build field-level updates array with only the changed fields
      const updates = Object.entries(editedFields).map(([markerId, fields]) => {
        const marker = markers.find(m => m.id === markerId);
        return {
          id: markerId,
          rowIndex: marker.rowIndex,
          fields // Only the fields that were edited
        };
      });

      console.log(`Syncing ${updates.length} markers with field-level updates`);

      // Dynamically calculate batch size based on payload size
      // Target: ~5MB per batch (well under 10MB limit with safety margin)
      const MAX_PAYLOAD_SIZE = 5 * 1024 * 1024; // 5MB in bytes

      // Estimate payload size for the first update to calculate batch size
      const estimatedSizePerUpdate = JSON.stringify(updates[0]).length;
      const calculatedBatchSize = Math.max(
        10, // Minimum 10 markers per batch
        Math.min(
          100, // Maximum 100 markers per batch
          Math.floor(MAX_PAYLOAD_SIZE / estimatedSizePerUpdate)
        )
      );

      console.log(`Estimated ${estimatedSizePerUpdate} bytes per update, using batch size of ${calculatedBatchSize}`);

      // Split into batches
      const batches = [];
      for (let i = 0; i < updates.length; i += calculatedBatchSize) {
        batches.push(updates.slice(i, i + calculatedBatchSize));
      }

      console.log(`Splitting into ${batches.length} batches`);

      // Send batches sequentially with minimal delay
      for (let i = 0; i < batches.length; i++) {
        console.log(`Syncing batch ${i + 1}/${batches.length} (${batches[i].length} markers)...`);
        await syncToSheet(sheetUrl, batches[i], selectedSheet || null);

        // Small delay between batches to be respectful
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      setHasUnsavedChanges(false);
      setEditedMarkerIds(new Set());
      setEditedFields({});
      alert(`Successfully synced ${updates.length} markers to Google Sheet!`);
    } catch (err) {
      console.error('Sync failed:', err);
      setError(err.message || 'Failed to sync to sheet');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectionChange = useCallback((selection) => {
    setSelectedMarkers(selection);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedMarkers(new Set());
  }, []);

  const mobileSheetOpen = isMobile && !filtersOpen && selectedMarkers.size > 0;

  const handleFilterChange = useCallback((filtered) => {
    setVisibleMarkers(filtered);
  }, []);

  const handleGroupVisibilityChange = useCallback(() => {
    // Auto-save group visibility changes if a sheet is loaded
    if (sheetUrl && sheetConfig && rawSheetData) {
      const colorAssignments = getColorAssignmentsObject();
      const hiddenGroups = getHiddenGroupsArray();
      saveRecentSheet({
        url: sheetUrl,
        sheetName: rawSheetData.sheetName || 'Unnamed Sheet',
        tabName: selectedSheet || 'Sheet1',
        config: { ...sheetConfig, colorAssignments, hiddenGroups }
      }).then(updatedRecent => {
        setPinnedSheets(updatedRecent.pinned || []);
        setRecentSheets(updatedRecent.recent || []);
      }).catch(error => {
        console.error('Failed to auto-save group visibility:', error);
      });
    }
  }, [sheetUrl, sheetConfig, rawSheetData, selectedSheet]);

  const handleSidebarWidthChange = useCallback((newWidth) => {
    setSidebarWidth(newWidth);
    localStorage.setItem('sidebarWidth', newWidth.toString());
  }, []);

  const handleColorChange = useCallback(() => {
    // Increment refresh key to trigger re-render of map
    setColorRefreshKey(prev => prev + 1);

    // Auto-save color changes if a sheet is loaded
    if (sheetUrl && sheetConfig && rawSheetData) {
      const colorAssignments = getColorAssignmentsObject();
      const hiddenGroups = getHiddenGroupsArray();
      saveRecentSheet({
        url: sheetUrl,
        sheetName: rawSheetData.sheetName || 'Unnamed Sheet',
        tabName: selectedSheet || 'Sheet1',
        config: { ...sheetConfig, colorAssignments, hiddenGroups }
      }).then(updatedRecent => {
        setPinnedSheets(updatedRecent.pinned || []);
        setRecentSheets(updatedRecent.recent || []);
      }).catch(error => {
        console.error('Failed to auto-save color changes:', error);
      });
    }
  }, [sheetUrl, sheetConfig, rawSheetData, selectedSheet]);

  const handleLoadSheetFromTile = useCallback(async (url, tabName, config) => {
    setSheetUrl(url);
    setSelectedSheet(tabName);
    setSheetConfig(config);
    setLoading(true);
    setError(null);

    // Restore color assignments and hidden groups if saved
    if (config.colorAssignments) {
      restoreColorAssignments(config.colorAssignments);
    }
    if (config.hiddenGroups) {
      restoreHiddenGroups(config.hiddenGroups);
    }

    try {
      const columnMapping = {
        latitude: config.latColumn,
        longitude: config.lngColumn,
      };

      const data = await fetchSheetData(url, columnMapping, tabName);

      setMarkers(data.markers);
      setRawSheetData(data);
      setHasUnsavedChanges(false);
      setEditedMarkerIds(new Set());
      setEditedFields({});

      // Update last accessed time and save color assignments and hidden groups
      try {
        const colorAssignments = getColorAssignmentsObject();
        const hiddenGroups = getHiddenGroupsArray();
        const updatedRecent = await saveRecentSheet({
          url,
          sheetName: data.sheetName || 'Unnamed Sheet',
          tabName,
          config: { ...config, colorAssignments, hiddenGroups }
        });
        setPinnedSheets(updatedRecent.pinned || []);
        setRecentSheets(updatedRecent.recent || []);
      } catch (error) {
        console.error('Failed to update recent sheets:', error);
      }
    } catch (err) {
      console.error('Failed to load sheet:', err);
      setError(err.message || 'Failed to load sheet data');
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePinSheet = useCallback(async (sheet) => {
    try {
      const updatedRecent = await pinSheet({
        url: sheet.url,
        sheetName: sheet.sheetName,
        tabName: sheet.tabName,
        config: sheet.config
      });
      setPinnedSheets(updatedRecent.pinned || []);
      setRecentSheets(updatedRecent.recent || []);
    } catch (error) {
      throw error;
    }
  }, []);

  const handleUnpinSheet = useCallback(async (url, tabName) => {
    try {
      const updatedRecent = await unpinSheet(url, tabName);
      setPinnedSheets(updatedRecent.pinned || []);
      setRecentSheets(updatedRecent.recent || []);
    } catch (error) {
      console.error('Failed to unpin sheet:', error);
    }
  }, []);

  const handleEditSheet = useCallback(async (url, tabName, newSheetName) => {
    try {
      const updatedRecent = await updateSheetName(url, tabName, newSheetName);
      setPinnedSheets(updatedRecent.pinned || []);
      setRecentSheets(updatedRecent.recent || []);
    } catch (error) {
      console.error('Failed to update sheet name:', error);
      setError(error.message || 'Failed to update sheet name');
    }
  }, []);

  const handleShareSheet = useCallback(async (savedMap) => {
    try {
      // If this is the currently loaded map, use current state; otherwise use saved config
      let configToShare = savedMap.config;
      if (savedMap.url === sheetUrl && savedMap.tabName === selectedSheet && markers.length > 0) {
        const colorAssignments = getColorAssignmentsObject();
        const hiddenGroups = getHiddenGroupsArray();
        configToShare = { ...savedMap.config, colorAssignments, hiddenGroups };
      }

      const result = await createSharedMap({
        sourceType: 'sheet',
        sourceUrl: savedMap.url,
        tabName: savedMap.tabName,
        mapName: savedMap.sheetName,
        config: configToShare
      });
      return result.shareUrl;
    } catch (error) {
      console.error('Failed to create share link:', error);
      throw error;
    }
  }, [sheetUrl, selectedSheet, markers]);

  console.log('About to return JSX');
  return (
    <div
      className={[
        'SheetMapper',
        isMobile && filtersOpen ? 'mobile-filters-open' : '',
        mobileSheetOpen ? 'mobile-detail-open' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="controls">
        <input
          type="text"
          placeholder="Enter Google Sheets URL..."
          value={sheetUrl}
          onChange={(e) => setSheetUrl(e.target.value)}
          className="sheet-url-input"
          disabled={loading}
        />
        <button
          onClick={handleLoadSheet}
          disabled={loading}
          className="load-button"
        >
          {loading ? 'Loading...' : 'Load Sheet'}
        </button>

        {markers.length > 0 && (
          <SyncButton
            onClick={handleSync}
            hasUnsavedChanges={hasUnsavedChanges}
            changesCount={editedMarkerIds.size}
            disabled={loading || !hasUnsavedChanges}
          />
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {markers.length > 0 && !showSetupDialog && (
        <div className="main-content">
          <Sidebar
            markers={markers}
            selectedMarkers={selectedMarkers}
            onSelectionChange={handleSelectionChange}
            onMarkerClick={(markerId) => {
              // Optionally center map on marker
              console.log('Marker clicked from sidebar:', markerId);
            }}
            onMarkerUpdate={handleMarkerUpdate}
            onFilterChange={handleFilterChange}
            onColorChange={handleColorChange}
            onGroupVisibilityChange={handleGroupVisibilityChange}
            sheetConfig={sheetConfig}
            selectionMode={selectionMode}
            onSelectMode={setSelectionMode}
            width={isMobile ? undefined : sidebarWidth}
            onWidthChange={handleSidebarWidthChange}
            onClose={isMobile ? (filtersOpen ? () => setFiltersOpen(false) : clearSelection) : undefined}
          />

          {isMobile && (
            <button
              type="button"
              className="sheet-mobile-backdrop"
              onClick={() => setFiltersOpen(false)}
              aria-label="Close markers panel"
            />
          )}

          <div className="map-container">
            {USE_DECKGL ? (
              <DeckGLMap
                markers={visibleMarkers}
                selectedMarkers={selectedMarkers}
                onMarkerUpdate={handleMarkerUpdate}
                onSelectionChange={handleSelectionChange}
                sheetConfig={sheetConfig}
                selectionMode={selectionMode}
                onSelectMode={setSelectionMode}
                colorRefreshKey={colorRefreshKey}
                onOpenSettings={handleOpenSettings}
              />
            ) : (
              <Map
                markers={visibleMarkers}
                selectedMarkers={selectedMarkers}
                onMarkerUpdate={handleMarkerUpdate}
                onSelectionChange={handleSelectionChange}
                sheetConfig={sheetConfig}
              />
            )}

            {isMobile && (
              <div className="sheet-mobile-actions">
                <button
                  type="button"
                  className="sheet-filters-fab"
                  onClick={() => setFiltersOpen(true)}
                >
                  Markers &amp; Filters
                  <span className="badge">{visibleMarkers.length.toLocaleString()}</span>
                </button>
                <button
                  type="button"
                  className="sheet-settings-fab"
                  onClick={handleOpenSettings}
                  aria-label="Sheet settings"
                >
                  ⚙️
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {markers.length === 0 && !loading && !showSetupDialog && !showTabSelectorDialog && (
        <div className="empty-state-with-sidebar">
          {recentSheets.length > 0 && (
            <RecentSheetsSidebar
              recentMaps={recentSheets}
              onLoadMap={(savedMap) => handleLoadSheetFromTile(savedMap.url, savedMap.tabName, savedMap.config)}
              onPinMap={handlePinSheet}
            />
          )}
          <div className="empty-state-main">
            {pinnedSheets.length > 0 && (
              <RecentSheetsTiles
                pinnedMaps={pinnedSheets}
                onLoadMap={(savedMap) => handleLoadSheetFromTile(savedMap.url, savedMap.tabName, savedMap.config)}
                onUnpinMap={handleUnpinSheet}
                onEditMap={handleEditSheet}
                onShareMap={handleShareSheet}
              />
            )}
            <div className="empty-state-message">
              <p>Enter a Google Sheets URL above to get started</p>
              <p className="help-text">
                Make sure your sheet has "latitude" and "longitude" columns
              </p>
            </div>
          </div>
        </div>
      )}

      {showTabSelectorDialog && (
        <div className="sheet-setup-overlay">
          <div className="sheet-setup-dialog" style={{ maxWidth: '600px' }}>
            <h2>📑 Select Sheet Tab</h2>
            <p className="setup-description">
              This spreadsheet has multiple tabs. Please select which one to load:
            </p>

            <div className="setup-section">
              <div className="tab-selector-list">
                {availableSheets.map(sheet => (
                  <label
                    key={sheet.name}
                    className={`tab-option ${selectedSheet === sheet.name ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="sheetTab"
                      value={sheet.name}
                      checked={selectedSheet === sheet.name}
                      onChange={(e) => setSelectedSheet(e.target.value)}
                    />
                    <div className="tab-info">
                      <strong>{sheet.name}</strong>
                      <span className="tab-row-count">{sheet.rowCount.toLocaleString()} rows</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="setup-actions">
              <button onClick={handleTabSelectorCancel} className="btn-cancel">
                Cancel
              </button>
              <button onClick={handleTabSelectorComplete} className="btn-submit">
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showSetupDialog && rawSheetData && rawSheetData.headers && (
        <SheetSetupDialog
          headers={rawSheetData.headers}
          markerCount={rawSheetData.markers ? rawSheetData.markers.length : 0}
          onComplete={handleSetupComplete}
          onCancel={handleSetupCancel}
          initialConfig={sheetConfig}
        />
      )}
    </div>
  );
}

export default SheetMapper;
